import React, { useEffect, useRef, useState } from 'react';
import styles from '../styles/BlogsLanding.module.scss';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Col from 'react-bootstrap/Col';
import CustomImage from "./CustomImage";
import Link from 'next/link';
import FeaturedBlogs from './FeaturedBlogs';
import { getBlogName, isFrappeOrJotFormClass } from '../utils/helpers';
import format from 'date-fns/format';
import Card from 'react-bootstrap/Card';
import {
  COACHING_CATEGORY_ENUM,
  formatOrder,
} from '../utils/constants';
import _debounce from 'lodash/debounce';
import fetchUtil from '../utils/fetchUtil';
import {
  fetchStudentDestinationCategories,
  fetchCourseCategories,
  fetchCoachingCategories,
  fetchSuccessStoriesCategories,
} from '../utils/blogCategoriesApi';
import { useRouter } from 'next/router';
import Select from 'react-select';
import Head from 'next/head';
import { getOnboardingOrFormCtaProps } from '../utils/applyCtaButtonProps';
import { persistSourceContext } from '../utils/b2c/sourceTracking';
import { PRIMARY_SOURCE_B2C_WIDGET } from '../utils/b2c/constants';

const BLOGS_PAGE_SIZE = 18;

interface FilterProps {
  searchValue: string;
  category: string;
  course: string;
  coaching: string;
  successStories: string;
  recruiters: boolean;
  institutes: boolean;
}

interface MobileFilterProps {
  category: string;
  course: string;
  coaching: string;
  successStories: string;
  recruiters: boolean;
  institutes: boolean;
}

const Blogs: React.FC = (props: any) => {
  const router = useRouter();
  const [blogsdata, setBlogsdata] = useState<any>([]);
  const [blogsCount, setBlogsCount] = useState<number>(0);
  const [clickFilter, setClickFilter] = useState<number>(0);
  const [filters, setFilters] = useState<FilterProps>({
    searchValue: '',
    category: '',
    course: '',
    coaching: '',
    successStories: '',
    recruiters: false,
    institutes: false,
  });

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<any>({});
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const { category, searchValue, course, coaching,  successStories, recruiters, institutes  } = filters;
  const [studentDestinationCategories, setStudentDestinationCategories] = useState<Array<any>>([]);
  const [courseCategories, setCourseCategories] = useState<Array<{label: string, value: string}>>([]);
  const [coachingCategories, setCoachingCategories] = useState<Array<{label: string, value: string}>>([]);
  const [successStoriesCategories, setSuccessStoriesCategories] = useState<Array<{label: string, value: string}>>([]);
  const [stickyHeaderActive, setStickyHeaderActive] = useState(false);

  const debounceSearchQuery = (searchKeyValue: string) => {
    const search = _debounce(
      () => setFilters({ ...filters, searchValue: searchKeyValue }),
      500
    );
    setSearchQuery((prevSearch: any) => {
      if (prevSearch.cancel) {
        prevSearch.cancel();
      }
      return search;
    });
    search();
  };

  const getBlogsFilterConditions = () => {
    const searchCondition = searchValue ? `&title_contains=${searchValue}` : '';
    const categoryCondition = category ? `&stu_dest_categories.category_contains=${category}` : '';
    const courseCondition = course ? `&courseCategory=${course}` : '';
    const coachingCondition = coaching ? `&coachingCategory=${COACHING_CATEGORY_ENUM[coaching] || coaching}` : '';
    const successStoriesCondition = successStories ? `&successStoriesCategory=${successStories}` : '';
    const recruiterCondition = recruiters ? `&forRecruiters=${recruiters}` : '';
    const institutesCondition = institutes ? `&forInstitutes=${institutes}` : '';
    return `${searchCondition}${categoryCondition}${courseCondition}${coachingCondition}${successStoriesCondition}${recruiterCondition}${institutesCondition}`;
  };

  useEffect(() => {
    const conditions = getBlogsFilterConditions();
    fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/blogs?_sort=date:DESC&_start=0&stu_dest_categories.category_ncontains=News&_limit=${BLOGS_PAGE_SIZE}${conditions}`,
      'GET',
      {},
      '',
      false
    ).then((res: any) => {
      setBlogsdata(res);
    });
    fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/blogs/count?_start=0&stu_dest_categories.category_ncontains=News${conditions}`,
      'GET',
      {},
      '',
      false
    ).then((res: any) => {
      setBlogsCount(res);
    });

  }, [filters]);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const handleViewMore = () => {
    const requestFilters = filters;
    setIsLoadingMore(true);
    const conditions = getBlogsFilterConditions();
    fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/blogs?_sort=date:DESC&_start=${blogsdata.length}&stu_dest_categories.category_ncontains=News&_limit=${BLOGS_PAGE_SIZE}${conditions}`,
      'GET',
      {},
      '',
      false
    ).then((res: any) => {
      if (filtersRef.current === requestFilters) {
        setBlogsdata((prev: any) => [...prev, ...res]);
      }
    }).finally(() => {
      setIsLoadingMore(false);
    });
  };


  useEffect(() => {
    fetchStudentDestinationCategories().then((res) => {
      setStudentDestinationCategories(res);
    });
  }, [])

  useEffect(() => {
    fetchCourseCategories().then((res) => {
      setCourseCategories(res);
    });
  }, [])

  useEffect(() => {
    fetchCoachingCategories().then((res) => {
      setCoachingCategories(res);
    });
  }, [])

  useEffect(() => {
    fetchSuccessStoriesCategories().then((res) => {
      setSuccessStoriesCategories(res);
    });
  }, [])

  const [show, setShow] = useState(false);
  const [selCategory, setSelCategory] = useState<MobileFilterProps>({
    category: '',
    course: '',
    coaching: '',
    successStories: '',
    recruiters: false,
    institutes: false,
  });

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    setStickyHeaderActive(scrollTop >= 1100); 
  };

  const handleClick = () => {
    if (stickyHeaderActive && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        scrollRef.current.style.paddingTop = '60px';
      }
  };

  // Helper function to get active filters with their display names
  const getActiveFilters = () => {
    const activeFilters: Array<{ type: string; label: string; value: string | boolean }> = [];
    
    if (filters.category) {
      activeFilters.push({
        type: 'category',
        label: filters.category,
        value: filters.category
      });
    }
    
    if (filters.course) {
      const courseLabel = getBlogName(filters.course, courseCategories) || filters.course;
      activeFilters.push({
        type: 'course',
        label: courseLabel,
        value: filters.course
      });
    }
    
    if (filters.coaching) {
      const coachingLabel = getBlogName(filters.coaching, coachingCategories) || filters.coaching;
      activeFilters.push({
        type: 'coaching',
        label: coachingLabel,
        value: filters.coaching
      });
    }
    
    if (filters.successStories) {
      const successStoriesLabel = getBlogName(filters.successStories, successStoriesCategories) || filters.successStories;
      activeFilters.push({
        type: 'successStories',
        label: successStoriesLabel,
        value: filters.successStories
      });
    }
    
    if (filters.recruiters) {
      activeFilters.push({
        type: 'recruiters',
        label: 'For Recruiters',
        value: true
      });
    }
    
    if (filters.institutes) {
      activeFilters.push({
        type: 'institutes',
        label: 'For Institutes',
        value: true
      });
    }
    
    return activeFilters;
  };

  // Function to remove individual filter
  const removeFilter = (filterType: string) => {
    setFilters({
      ...filters,
      [filterType]: filterType === 'recruiters' || filterType === 'institutes' ? false : ''
    });
    handleClick();
  };

  const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
    btnLink: props?.pageData?.bannerBtn?.btnLink,
    formId: props?.pageData?.bannerBtn?.formId,
    toOnboardingEnabled: props?.pageData?.bannerBtn?.toOnboardingEnabled,
  });

  const enquireNowClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (applyCtaButtonProps.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: props?.pageData?.bannerBtn?.onBoardingFlow?.nearestOffice ?? true,
        coachingVisible: props?.pageData?.bannerBtn?.onBoardingFlow?.lookingForCoaching ?? true, // CFM-1514
      });

    }

    applyCtaButtonProps.onClick?.(e)
  };

  return (
    <>
    <Head>
      <link href="/images/newsandpress/news-bg.svg" rel="preload" as="image" fetchpriority="high" />
      <link href={blogsdata?.[0]?.newimageUrl?.imageUrl} rel="preload" as="image" fetchpriority="high" />
    </Head>
    <div className={styles.newpress}>
      <div className={styles.curve}>
        <div id='blogheading' className={styles.content}>
          <Container>
            <Row className='justify-content-center'>
              <Col lg={8} md={8} xs={12}>
                <div className='d-block d-sm-none text-center'>
                  <div className={styles.breadcrum}>
                    <Breadcrumb className={styles.mobileBreadCrum}>
                      <Breadcrumb.Item style={{ opacity: 0.5 }} href='/'>
                        Home
                      </Breadcrumb.Item>
                      <Breadcrumb.Item href=''>Blogs</Breadcrumb.Item>
                    </Breadcrumb>
                  </div>
                </div>
                <div className={styles.heading} ref={scrollRef}>
                  <div className={styles.breadcrum}>
                    <Breadcrumb className='d-none d-sm-block'>
                      <Breadcrumb.Item style={{ opacity: 0.5 }} href='/'>
                        Home
                      </Breadcrumb.Item>
                      <Breadcrumb.Item href=''>Blog</Breadcrumb.Item>
                    </Breadcrumb>
                  </div>
                  <h1>{props?.pageData?.title}</h1>
                  <p>{props?.pageData?.description}</p>
                  <Button
                    id='enquirebtn'
                    variant='primary'
                    size='lg'
                    className={`${styles.enquirebtn} btn ${applyCtaButtonProps.formClassName}`}
                    onClick={enquireNowClickHandler}
                  >
                    {props?.pageData?.bannerBtn?.label || 'Enquire Now'}
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <div className={styles.BlogLandingFeaturedNews}>
        <FeaturedBlogs />
      </div>

      <div  ref={scrollRef}></div>
      <div id='latestblogs' className={styles.latestNew}>
        <Container>
          <div className={styles.customContainer}>
          <Row>
            <div className={styles.heading}>
              <h2>All Blogs</h2>
            </div>
          </Row>
          <div className={styles.news}>
            <div className={`${styles.filtersDrop} ${styles.filterTop} ${stickyHeaderActive ? styles.isSticky : ''}`}id="stickyHeader" >
             <div>
              
             </div>
              <Container className={styles.container}>
                <Row className={`${styles.topFilter} justify-content-center'`}>
                  <Col xl={12} lg={12} md={12} sm={12} xs={12} className={styles.filtercheck}>
                    <div className={styles.selectdiv}>
                      <div
                        className={`${styles.Selectfilt} ${clickFilter === 1 ? styles.activeSelect : ''
                          } ${filters.category && styles.selectedFilter}`}
                        onClick={() =>
                          !clickFilter ? setClickFilter(1) : setClickFilter(0)
                        }
                      >
                        Study Destinations
                        <CustomImage
                          src={
                            clickFilter === 1
                              ? '/uparrowblue.svg'
                              : '/downarrowgr.svg'
                          }
                          width={20}
                          height={8}
                          alt='uparrowblue.svg'
                        />
                      </div>
                      <div
                        className={`${styles.Selectfilt} ${clickFilter === 2 ? styles.activeSelect : ''
                          } ${filters.course && styles.selectedFilter}`}
                        onClick={() =>
                          !clickFilter ? setClickFilter(2) : setClickFilter(0)
                        }
                      >
                        Courses
                        <CustomImage
                          src={
                            clickFilter === 2
                              ? '/uparrowblue.svg'
                              : '/downarrowgr.svg'
                          }
                          width={20}
                          height={8}
                          alt='uparrowblue.svg'
                        />
                      </div>
                      <div
                        className={`${styles.Selectfilt} ${clickFilter === 3 ? styles.activeSelect : ''
                          } ${filters.coaching && styles.selectedFilter}`}
                        onClick={() =>
                          !clickFilter ? setClickFilter(3) : setClickFilter(0)
                        }
                      >
                        Coaching
                        <CustomImage
                          src={
                            clickFilter === 3
                              ? '/uparrowblue.svg'
                              : '/downarrowgr.svg'
                          }
                          width={20}
                          height={8}
                          alt='uparrowblue.svg'
                        />
                      </div>
                      <div
                        className={`${styles.Selectfilt} ${clickFilter === 4 ? styles.activeSelect : ''
                          } ${filters.successStories && styles.selectedFilter}`}
                        onClick={() =>
                          !clickFilter ? setClickFilter(4) : setClickFilter(0)
                        }
                      >
                        Success Stories
                        <CustomImage
                          src={
                            clickFilter === 4
                              ? '/uparrowblue.svg'
                              : '/downarrowgr.svg'
                          }
                          width={20}
                          height={8}
                          alt='uparrowblue.svg'
                        />
                      </div>
                      <div
                        className={`${styles.Selectfilt} ${styles.recruiterFilter
                          } ${filters.recruiters === true &&
                          styles.activeRecruiterFilter
                          }`}
                        onClick={() => {
                          setClickFilter(0);
                          setFilters({
                            ...filters,
                            recruiters: !recruiters,
                          });
                          //scrollRef?.current?.scrollIntoView();
                        }}
                      >
                        For Recruiters
                      </div>
                      <div
                        className={`${styles.Selectfilt} ${styles.recruiterFilter
                          } ${filters.institutes === true &&
                          styles.activeRecruiterFilter
                          }`}
                        onClick={() => {
                          setClickFilter(0);
                          setFilters({
                            ...filters,
                            institutes: !institutes,
                          });
                          //scrollRef?.current?.scrollIntoView();
                        }}
                      >
                        For Institutes
                      </div>
                      {/* <div
                        className={`${styles.Selectfilt}`}
                        onClick={() => {
                          router.push('/news');
                        }}
                      >
                        News
                      </div> */}
                      <div
                        className={styles.clearFilter}
                        onClick={() => {
                          setFilters({
                            searchValue: '',
                            category: '',
                            course: '',
                            coaching: '',
                            successStories: '',
                            recruiters: false,
                            institutes: false,
                          });
                          setSearchKeyword('');
                          setClickFilter(0);
                          //scrollRef?.current?.scrollIntoView();
                        }}
                      >
                        <h6>Clear Filter</h6>
                      </div>
                      <div className={styles.searchWrapInRow}>
                        <form className='navbar-form' role='search'>
                          <div className='input-group add-on'>
                            <div className={styles.inputBox}>
                              <input
                                className='form-control'
                                placeholder='Search Blogs'
                                name='srch-term'
                                id='srch-term'
                                type='text'
                                onChange={(e) => {
                                  const searchValues = e.target.value;
                                  debounceSearchQuery(searchValues);
                                  setSearchKeyword(searchValues);
                                  scrollRef?.current?.scrollIntoView();
                                }}
                                value={searchKeyword}
                              />
                              <div className={styles.inputBtn}
                              >
                                <CustomImage
                                  src='/searchco.svg'
                                  alt='searchco'
                                  width={14}
                                  height={14}
                                  onClick={() =>
                                    scrollRef?.current?.scrollIntoView()
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </Col>
                  <Col lg={3} md={3}  sm={2} xs={3} className={styles.mobilemenu}>
                    <div
                      className={styles.btn}
                      onClick={() => {
                        handleShow();
                        setClickFilter(1);
                      }}
                    >
                      <CustomImage
                        src='/new-images/threeline-menu.svg'
                        alt='threelineMenu'
                        width={18}
                        height={12}
                      />
                    </div>
                  </Col>
                </Row>
                {/* Selected Filters Chips Section */}
                {getActiveFilters().length > 0 && (
                  <Row className={styles.topFilter}>
                    <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div className={styles.selectedFiltersChips}>
                        {getActiveFilters().map((filter, index) => (
                          <div key={index} className={styles.selectedFilterChip}>
                            <span className={styles.chipText}>{filter.label}</span>
                            <button
                              className={styles.chipRemove}
                              onClick={() => removeFilter(filter.type)}
                              aria-label={`Remove ${filter.label} filter`}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 4L4 12" stroke="#226CF5" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M4 4L12 12" stroke="#226CF5" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                )}
                {/* WEB-538: Dropdown content expands header height - positioned below filter buttons row */}
                {/* WEB-538: Wrapped in Row with topFilter class to align with filter buttons */}
                {clickFilter === 1 && (
                  <Row className={styles.topFilter}>
                    <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div className={styles.filterSelectGrid}>
                        <div className={styles.filterSelectGridContainer}>
                          {studentDestinationCategories?.map(
                            (type: any, index: number) =>
                              index !== 10 && (
                                <div
                                  key={index}
                                  className={`${styles.filterSelectGridItem} ${filters.category === type.category ? styles.filterSelectGridItemSelected : ''}`}
                                  onClick={() => {
                                    setFilters({
                                      ...filters,
                                      category: type.category,
                                    });
                                    setClickFilter(0);
                                    handleClick();
                                  }}
                                >
                                  <span className={styles.filterSelectGridText}>
                                    {type.category}
                                  </span>
                                  {type.count && (
                                    <span className={styles.filterSelectGridBadge}>
                                      {type.count}
                                    </span>
                                  )}
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
                {clickFilter === 2 && (
                  <Row className={styles.topFilter}>
                    <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div className={styles.filterSelectGrid}>
                        <div className={styles.filterSelectGridContainer}>
                          {courseCategories.map(
                            (type: any, index: number) => (
                              <div
                                key={index}
                                className={`${styles.filterSelectGridItem} ${filters.course === type.value ? styles.filterSelectGridItemSelected : ''}`}
                                onClick={() => {
                                  setFilters({
                                    ...filters,
                                    course: type.value,
                                  });
                                  setClickFilter(0);
                                  handleClick();
                                }}
                              >
                                <span className={styles.filterSelectGridText}>
                                  {type.label}
                                </span>
                                {type.count && (
                                  <span className={styles.filterSelectGridBadge}>
                                    {type.count}
                                  </span>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
                {clickFilter === 3 && (
                  <Row className={styles.topFilter}>
                    <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div className={styles.filterSelectGrid}>
                        <div className={styles.filterSelectGridContainer}>
                          {coachingCategories.map(
                            (type: any, index: number) => (
                              <div
                                key={index}
                                className={`${styles.filterSelectGridItem} ${filters.coaching === type.value ? styles.filterSelectGridItemSelected : ''}`}
                                onClick={() => {
                                  setFilters({
                                    ...filters,
                                    coaching: type.value,
                                  });
                                  setClickFilter(0);
                                  handleClick();
                                }}
                              >
                                <span className={styles.filterSelectGridText}>
                                  {type.label}
                                </span>
                                {type.count && (
                                  <span className={styles.filterSelectGridBadge}>
                                    {type.count}
                                  </span>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
                {clickFilter === 4 && (
                  <Row className={styles.topFilter}>
                    <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div className={styles.filterSelectGrid}>
                        <div className={styles.filterSelectGridContainer}>
                          {successStoriesCategories.map(
                            (type: any, index: number) => (
                              <div
                                key={index}
                                className={`${styles.filterSelectGridItem} ${filters.successStories === type.value ? styles.filterSelectGridItemSelected : ''}`}
                                onClick={() => {
                                  setFilters({
                                    ...filters,
                                    successStories: type.value,
                                  });
                                  setClickFilter(0);
                                  handleClick();
                                }}
                              >
                                <span className={styles.filterSelectGridText}>
                                  {type.label}
                                </span>
                                {type.count && (
                                  <span className={styles.filterSelectGridBadge}>
                                    {type.count}
                                  </span>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </Container>
              <Modal
                show={show}
                onHide={handleClose}
                className={styles.filtCat}
                centered
              >
                <Modal.Body className={`show-grid ${styles.modalBodyScroll}`}>
                  <Container className={styles.contain}>
                    <Row className={styles.row}>
                      <Col xs={3} md={3} sm={3} className={styles.col}>
                        <div
                          className={`${styles.catfilter} ${clickFilter === 1 && styles.catSelected
                            }`}
                          onClick={() => setClickFilter(1)}
                        >
                          Study Destinations
                        </div>
                      </Col>
                      <Col xs={2} md={2} sm={2} className={styles.col}>
                        <div
                          className={`${styles.catfilter} ${clickFilter === 2 && styles.catSelected
                            }`}
                          onClick={() => setClickFilter(2)}
                        >
                          Courses
                        </div>
                      </Col>
                      <Col xs={2} md={2} sm={2} className={styles.col}>
                        <div
                          className={`${styles.catfilter} ${clickFilter === 3 && styles.catSelected
                            }`}
                          onClick={() => setClickFilter(3)}
                        >
                          Coaching
                        </div>
                      </Col>
                      <Col xs={2} md={2} sm={2} className={styles.col}>
                        <div
                          className={`${styles.catfilter} ${clickFilter === 4 && styles.catSelected
                            }`}
                          onClick={() => setClickFilter(4)}
                        >
                          Success Stories
                        </div>
                      </Col>
                      <Col xs={2} md={2} sm={2} className={styles.col}>
                        <div
                          className={`${styles.catfilter} ${styles.recruiterFilter
                            } ${selCategory.recruiters === true &&
                            styles.activeRecruiterFilter
                            }`}
                          onClick={() => {
                            setClickFilter(4);
                            setSelCategory({
                              ...selCategory,
                              recruiters: !selCategory.recruiters,
                            });
                          }}
                        >
                          For Recruiter
                        </div>
                      </Col>
                      <Col xs={2} md={2} sm={2} className={styles.col}>
                        <div
                          className={`${styles.catfilter} ${styles.recruiterFilter
                            } ${selCategory.institutes === true &&
                            styles.activeRecruiterFilter
                            }`}
                          onClick={() => {
                            setClickFilter(4);
                            setSelCategory({
                              ...selCategory,
                              institutes: !selCategory.institutes,
                            });
                          }}
                        >
                          For Institutes
                        </div>
                      </Col>
                      {/* <Col xs={2} md={2} sm={2} className={styles.col}>
                        <div
                          className={`${styles.catfilter}`}
                          onClick={() => router.push('/news')}
                        >
                          News
                        </div>
                      </Col> */}
                    </Row>
                    <Row>
                      {/* WEB-538: Changed mobile view from 3-column (xs={4}) to 2-column (xs={6}) grid layout */}
                      {clickFilter === 1 &&
                        studentDestinationCategories?.map(
                          (type: any, index: number) =>
                            type.category !== 'News' && (
                              <Col xs={6} md={6} sm={6} key={index}>
                                <div
                                  className={`${styles.optfilter} ${selCategory.category === type.category
                                    ? styles.optSelected
                                    : ''
                                    }`}
                                  onClick={() =>
                                    setSelCategory({
                                      ...selCategory,
                                      category: type.category,
                                    })
                                  }
                                >
                                  {type.category}
                                </div>
                              </Col>
                            )
                        )}
                      {/* WEB-538: Changed mobile view from 3-column (xs={4}) to 2-column (xs={6}) grid layout */}
                      {clickFilter === 2 &&
                        courseCategories.map((type: any, index: number) => (
                          <Col xs={6} md={6} sm={6} key={index}>
                            <div
                              className={`${styles.optfilter} ${selCategory.course === type.value
                                ? styles.optSelected
                                : ''
                                }`}
                              onClick={() =>
                                setSelCategory({
                                  ...selCategory,
                                  course: type.value,
                                })
                              }
                            >
                              {type.label}
                            </div>
                          </Col>
                        ))}
                      {/* WEB-538: Changed mobile view from 3-column (xs={4}) to 2-column (xs={6}) grid layout */}
                      {clickFilter === 3 &&
                        coachingCategories.map((type: any, index: number) => (
                          <Col xs={6} md={6} sm={6} key={index}>
                            <div
                              className={`${styles.optfilter} ${selCategory.coaching === type.value
                                ? styles.optSelected
                                : ''
                                }`}
                              onClick={() =>
                                setSelCategory({
                                  ...selCategory,
                                  coaching: type.value,
                                })
                              }
                            >
                              {type.label}
                            </div>
                          </Col>
                        ))}
                      {/* WEB-538: Changed mobile view from 3-column (xs={4}) to 2-column (xs={6}) grid layout */}
                      {clickFilter === 4 &&
                        successStoriesCategories.map((type: any, index: number) => (
                          <Col xs={6} md={6} sm={6} key={index}>
                            <div
                              className={`${styles.optfilter} ${selCategory.successStories === type.value
                                ? styles.optSelected
                                : ''
                                }`}
                              onClick={() =>
                                setSelCategory({
                                  ...selCategory,
                                  successStories: type.value,
                                })
                              }
                            >
                              {type.label}
                            </div>
                          </Col>
                        ))}
                    </Row>
                  </Container>
                </Modal.Body>
                <Modal.Footer className={styles.footerSpace}>
                  {/* WEB-538: Close button for mobile view - matches Figma design */}
                  <Button
                    className={styles.btnClose}
                    variant='link'
                    onClick={() => {
                      handleClose();
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    className={styles.btnsecondary}
                    variant='secondary'
                    onClick={() => {
                      setFilters({
                        searchValue: '',
                        category: '',
                        course: '',
                        coaching: '',
                        successStories: '',
                        recruiters: false,
                        institutes: false,
                      });
                      setSearchKeyword('');
                      setClickFilter(0);
                      setSelCategory({
                        category: '',
                        course: '',
                        coaching: '',
                        successStories: '',
                        recruiters: false,
                        institutes: false,
                      });
                      handleClose();
                      scrollRef?.current?.scrollIntoView();
                    }}
                  >
                    Clear Filter
                  </Button>
                  <Button
                    className={styles.btnprimary}
                    variant='primary'
                    onClick={() => {
                      setFilters({ ...filters, ...selCategory });
                      handleClose();
                      scrollRef?.current?.scrollIntoView();
                    }}
                  >
                    Apply Filters
                  </Button>
                </Modal.Footer>
              </Modal>
           
            </div>
            <div className={styles.allBlogs}>
              <Row>
                {blogsdata.length > 0
                  ? blogsdata.map((blog: any, index: number) => {
                    return (
                     <>
                    <Col xxl={4} xl={4} lg={6} md={6} sm={12} key={index}>
                        <a href={`/blogs/${blog.slug}`}>
                          <Card className={styles.card}>
                            <div className={styles.latestBlogImg}>
                              <Card.Img variant='top'  
                                src={blog.blogLandingCardImage?.imageUrl ? blog.blogLandingCardImage.imageUrl : blog.newimageUrl?.imageUrl} 
                                alt={blog.blogLandingCardImage?.alt ? blog.blogLandingCardImage.alt : blog.newimageUrl?.alt}  
                                object-fit="fill"/>
                            </div>
                            <Card.Body className={styles.cardBody}>
                             {blog.category && (
                               <h6 className={styles.blogsCategories}>{blog.category}</h6>
                              )}
                              <h5>{blog.title}</h5>
                              {blog.shortDescription && (
                                <p className={styles.shortDescription}>{blog.shortDescription}</p>
                              )}
                              <div className={styles.bottom}>
                              <span> {format(new Date(blog.date), formatOrder)}</span>
                                <a href={`/blogs/${blog.slug}`}> Read More </a> 
                              </div>
                            </Card.Body>
                          </Card>
                        </a>
                      </Col>
                     </>
                    );
                  })
                  : <p className={styles.noBlogs}>No Blogs</p>}
              </Row>
            </div>
            {blogsdata.length < blogsCount && !isLoadingMore && (
              <Row className='text-center'>
                <Col>
                <div
                  onClick={handleViewMore}
                  className={styles.viewMore}
                >
                  View More
                </div>
                </Col>
              </Row>
            )}
          </div>
          {isLoadingMore && <div className={`${styles.loading} text-center`}>Loading...</div>}
          </div>
        </Container>
      </div>

      

      <div id='whykc' className={styles.WhyKc}>
        <Container>
          <div className={styles.customContainer}>
            <Row>
              <div className='text-center'>
                <h3>Learn more about KC Overseas</h3>
              </div>
            </Row>
            <Row>
              {props?.pageData?.learnMore?.length > 0 &&
                props?.pageData?.learnMore.map((data: any, index: number) => {
                  return (
                    <Col xl={4} md={4} xs={12} key={index}>
                      <div className='text-center mb-3'>
                        <div className={styles.img}>
                          <CustomImage
                            src={data.imageUrl}
                            alt='About-Us'
                            width={90}
                            height={90}
                          />
                        </div>
                        <h6>{data.title}</h6>
                        <p>{data.description}</p>
                        <Link href={data.link.href}>
                          <a className={styles.viewMore}>{data.link.label}</a>
                        </Link>
                      </div>
                    </Col>
                  );
                })}
            </Row>
          </div>
        </Container>
      </div>
    </div>
    </>
  );
};

export default Blogs;
